import React, { useState, useEffect, useRef } from 'react';
import { ImageUpload } from './ImageUpload';
import { ImagePreview, ImagePreviewData } from './ImagePreview';
import {
  generateImageId,
  optimizeImage,
  generateThumbnail,
  generateVideoThumbnail,
  isVideoFile,
  createPreviewUrl,
  revokePreviewUrl,
} from './imageUtils';
import { registerPendingFile, hasPendingFile, removePendingFile } from './pendingFiles';
import type { MediaItem } from './types';

interface ImageGalleryProps {
  images: MediaItem[];
  onChange: (images: MediaItem[]) => void;
  maxImages?: number;
  className?: string;
}

interface InternalImage extends ImagePreviewData {
  file?: File;
  originalUrl: string;
  caption?: string;
}

/** Check if a URL is displayable */
function isValidUrl(url: string): boolean {
  if (!url) return false;
  if (url.startsWith('blob:')) return hasPendingFile(url);
  return url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://');
}

/** Try to extract a filename from an http URL */
function extractFilename(url: string, fallback: string): string {
  if (url.startsWith('http')) {
    try {
      const pathname = new URL(url).pathname;
      const last = pathname.split('/').pop();
      if (last) return decodeURIComponent(last);
    } catch { /* ignore */ }
  }
  return fallback;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  onChange,
  maxImages = 10,
  className = '',
}) => {
  const [internalImages, setInternalImages] = useState<InternalImage[]>([]);
  const [draggedImage, setDraggedImage] = useState<InternalImage | null>(null);
  const initializedRef = useRef(false);
  const lastPropKeyRef = useRef<string | null>(null);
  const lastSentKeyRef = useRef<string | null>(null);
  const skipNextSyncRef = useRef(false);
  const onChangeRef = useRef(onChange);

  // Keep onChangeRef always pointing to the latest callback
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // ── Init from props (on mount and when props change externally) ──
  useEffect(() => {
    const propKey = images.map(i => i.url + '::' + (i.copyright ?? '')).join('|||');
    if (propKey === lastPropKeyRef.current) return;
    lastPropKeyRef.current = propKey;

    const valid = images.filter(img => isValidUrl(img.url));

    if (valid.length > 0) {
      const loaded: InternalImage[] = valid.map((img, i) => ({
        id: generateImageId(),
        url: img.url,
        originalUrl: img.url,
        filename: extractFilename(img.url, img.caption || `Image ${i + 1}`),
        size: 0,
        isMain: i === 0,
        caption: img.caption,
        copyright: img.copyright,
      }));
      setInternalImages(loaded);
      skipNextSyncRef.current = true;
      // Also set lastSentKeyRef so the sync effect won't re-fire onChange
      // with the same data after skipNextSyncRef is consumed
      lastSentKeyRef.current = propKey;
    } else if (!initializedRef.current) {
      setInternalImages([]);
      lastSentKeyRef.current = '';
    }

    initializedRef.current = true;
  }, [images]);

  // ── Sync internal → parent (fires after user actions like upload/delete/reorder) ──
  useEffect(() => {
    if (!initializedRef.current) return;

    if (skipNextSyncRef.current) { skipNextSyncRef.current = false; return; }
    const ready = internalImages
      .filter((img) => !img.isUploading && !img.error && img.originalUrl)
      .map((img) => ({
        url: img.originalUrl,
        caption: img.caption,
        copyright: img.copyright,
      }));

    const newKey = ready.map(i => i.url + '::' + (i.copyright ?? '')).join('|||');
    if (newKey !== lastSentKeyRef.current) {
      lastSentKeyRef.current = newKey;
      onChangeRef.current(ready);
    }
  }, [internalImages]);

  const handleCopyrightChange = (id: string, copyright: string) => {
    setInternalImages((prev) =>
      prev.map((img) => (img.id === id ? { ...img, copyright } : img))
    );
  };

  // Handle new file selection — ALL files become blob URLs (lightweight in form state)
  const handleFilesSelected = async (files: File[]) => {
    const newImages: InternalImage[] = files.map((file) => ({
      id: generateImageId(),
      url: createPreviewUrl(file), // temp blob preview while processing
      originalUrl: '',
      filename: file.name,
      size: file.size,
      file,
      isUploading: true,
      progress: 0,
      isMain: internalImages.length === 0,
    }));

    setInternalImages((prev) => [...prev, ...newImages]);

    for (const newImage of newImages) {
      try {
        const file = newImage.file!;
        const isVideo = isVideoFile(file);

        // Generate thumbnail
        const thumbnailUrl = isVideo
          ? await generateVideoThumbnail(file)
          : await generateThumbnail(file);

        setInternalImages((prev) =>
          prev.map((img) =>
            img.id === newImage.id ? { ...img, thumbnail: thumbnailUrl, progress: 30 } : img
          )
        );

        // Optimize (skipped for GIFs and videos)
        const optimizedFile = await optimizeImage(file);

        setInternalImages((prev) =>
          prev.map((img) =>
            img.id === newImage.id ? { ...img, progress: 60 } : img
          )
        );

        // Create blob URL and register the File for later retrieval
        const blobUrl = createPreviewUrl(optimizedFile);
        registerPendingFile(blobUrl, optimizedFile);

        setInternalImages((prev) =>
          prev.map((img) =>
            img.id === newImage.id
              ? { ...img, originalUrl: blobUrl, url: blobUrl, isUploading: false, progress: 100 }
              : img
          )
        );

        // Clean up the temporary preview blob URL (different from the final one)
        if (newImage.url !== blobUrl) {
          revokePreviewUrl(newImage.url);
        }
      } catch (error) {
        console.error('Upload failed:', error);
        setInternalImages((prev) =>
          prev.map((img) =>
            img.id === newImage.id
              ? { ...img, isUploading: false, error: 'Upload failed. Please try again.' }
              : img
          )
        );
      }
    }
  };

  // Handle image deletion
  const handleDelete = (id: string) => {
    setInternalImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      if (filtered.length > 0 && !filtered.some((img) => img.isMain)) {
        filtered[0].isMain = true;
      }
      // Clean up blob URLs and pending file registrations
      const deleted = prev.find((img) => img.id === id);
      if (deleted?.originalUrl?.startsWith('blob:')) removePendingFile(deleted.originalUrl);
      if (deleted?.url?.startsWith('blob:') && deleted.url !== deleted.originalUrl) revokePreviewUrl(deleted.url);
      if (deleted?.thumbnail?.startsWith('blob:')) revokePreviewUrl(deleted.thumbnail);
      return filtered;
    });
  };

  // Handle retry upload
  const handleRetry = async (id: string) => {
    const image = internalImages.find((img) => img.id === id);
    if (!image?.file) return;

    setInternalImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, error: undefined, isUploading: true, progress: 0 } : img
      )
    );

    try {
      const optimizedFile = await optimizeImage(image.file);
      setInternalImages((prev) =>
        prev.map((img) => (img.id === id ? { ...img, progress: 50 } : img))
      );

      const blobUrl = createPreviewUrl(optimizedFile);
      registerPendingFile(blobUrl, optimizedFile);
      setInternalImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? { ...img, originalUrl: blobUrl, url: blobUrl, isUploading: false, progress: 100 }
            : img
        )
      );
    } catch (error) {
      setInternalImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? { ...img, error: 'Processing failed. Please try again.', isUploading: false }
            : img
        )
      );
    }
  };

  // Drag and drop reordering
  const handleDragStart = (e: React.DragEvent, image: ImagePreviewData) => {
    const internalImg = internalImages.find(img => img.id === image.id);
    if (internalImg) {
      setDraggedImage(internalImg);
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetImage: ImagePreviewData) => {
    e.preventDefault();

    if (!draggedImage || draggedImage.id === targetImage.id) {
      setDraggedImage(null);
      return;
    }

    setInternalImages((prev) => {
      const newImages = [...prev];
      const draggedIndex = newImages.findIndex((img) => img.id === draggedImage.id);
      const targetIndex = newImages.findIndex((img) => img.id === targetImage.id);

      // Swap images
      const [removed] = newImages.splice(draggedIndex, 1);
      newImages.splice(targetIndex, 0, removed);

      // Update main image (first one is always main)
      return newImages.map((img, idx) => ({
        ...img,
        isMain: idx === 0,
      }));
    });

    setDraggedImage(null);
  };

  return (
    <div className={className}>
      {/* Image grid */}
      {internalImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          {internalImages.map((image) => (
            <ImagePreview
              key={image.id}
              image={image}
              onDelete={handleDelete}
              onRetry={handleRetry}
              onCopyrightChange={handleCopyrightChange}
              draggable={!image.isUploading && !image.error}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {/* Upload zone */}
      {internalImages.length < maxImages && (
        <ImageUpload
          onFilesSelected={handleFilesSelected}
          maxImages={maxImages}
          currentCount={internalImages.filter((img) => !img.error).length}
        />
      )}

      {/* Helper text */}
      {internalImages.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          💡 Tip: Drag images to reorder. The first image will be the main event image.
        </p>
      )}

    </div>
  );
};
